import React, { useState, useEffect } from "react";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    IconButton,
    InputBase,
    Popper,
    Paper,
    FormControl,
    FormControlLabel,
    Checkbox,
    Divider,
    FormGroup
} from "@mui/material";
import { useTheme } from "@emotion/react";
import FilterIcon from "@mui/icons-material/FilterList";
import { Select } from "./Custom Inputs/Select";
import { useDispatch, useSelector } from "react-redux";
import { BasicTooltip } from "./StyledComponents";
import {
    updateQuickSearch,
    updateQuickSearchFilter,
} from "../../js/redux/actions/userPref";
import { useHistory } from "react-router-dom";
import { t } from "react-i18nify";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { randomID } from "../../routers/MenuRouter";
import { historyPush } from "../../js/utils/genericMethods";
import Axios from "axios";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { fetchNewEntityTreeView } from "../../js/utils/backend";

const defaultSearchCategories = [
    { label: "Entities", submenu: [], value: "Entities", checked: true },
    { label: "Documents", value: "Documents", checked: true },
    { label: "Stations", value: "Stations", checked: true },
    { label: "Zones", value: "Zones", checked: true },
    { label: "Samplings", value: "Samplings", checked: true },
    { label: "Branches", value: "Branches", checked: true },
];

const maxResultOptions = [
    { label: "5", value: 5 },
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
];

const SearchBar = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [newEntityTreeView, setNewEntityTreeView] = useState(false);
    const token = useSelector(state => state.auth.Token);
    const entityIds = useSelector(state => state.auth.EntityManagerEntityIDs);
    const { CompanyManager, HoldingManager } = useSelector(state => state.auth);
    const searchInput = useSelector(state => state.userPref.QuickSearch);
    const [searchText, setSearchText] = useState(searchInput);
    const history = useHistory();
    const quickSearchFilter = useSelector(
        state => state.userPref.QuickSearchFilter
    );
    const openPopper = Boolean(anchorEl);
    const id = openPopper ? "simple-popper" : undefined;

    const handlePopper = event => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const [searchCategories, setSearchCategories] = useState(
        quickSearchFilter?.SearchCategories
            ? quickSearchFilter?.SearchCategories
            : defaultSearchCategories
    );

    function updateCategories(e) {
        let updated;
        if(newEntityTreeView){
            updated = searchCategories?.map((obj) => {
                if (obj.label === e.target.name) {
                    const updatedObj = { ...obj, checked: e.target.checked };
                    if (obj.label === "Entities" && updatedObj.submenu) {
                        updatedObj.submenu = updatedObj.submenu.map((branch) => ({
                            ...branch,
                            Countries: branch.Countries.map((country) => ({
                                ...country,
                                OTEntities: country.OTEntities.map((entity) => ({
                                    ...entity,
                                    Checked: updatedObj.checked,
                                })),
                            })),
                        }));
                    }
                    return updatedObj;
                } else if (obj.submenu) {
                    // Handle individual entities inside branches
                    const updatedSubmenu = obj.submenu.map((branch) => {
                        const updatedCountries = branch.Countries.map((country) => {
                            const updatedEntities = country.OTEntities.map((entity) =>
                                entity.Label === e.target.name
                                    ? { ...entity, Checked: e.target.checked }
                                    : entity
                            );
        
                            // Check if at least one entity is checked
                            const isCountryChecked = updatedEntities.some(
                                (entity) => entity.Checked
                            );
        
                            return { ...country, OTEntities: updatedEntities, Checked: isCountryChecked };
                        });
        
                        // Check if at least one country is checked
                        const isBranchChecked = updatedCountries.some(
                            (country) => country.Checked
                        );
        
                        return { ...branch, Countries: updatedCountries, Checked: isBranchChecked };
                    });
        
                    // Check if at least one branch is checked
                    const isCategoryChecked = updatedSubmenu.some(
                        (branch) => branch.Checked
                    );
        
                    return { ...obj, checked: isCategoryChecked, submenu: updatedSubmenu };
                } else {
                    return obj;
                }
            });
        }
        else{
            updated = searchCategories?.map(obj => {
                if (obj.label === e.target.name) {
                    const updatedObj = { ...obj, checked: e.target.checked };
                    if(obj.label === "Entities" && updatedObj.submenu){
                        for(let i=0; i<updatedObj.submenu.length; i++){
                            updatedObj.submenu[i].checked = updatedObj.checked;
                        }
                    }
                    return updatedObj;
                } else if (obj.submenu) {
                    let counter = 0;
                    const updatedSubmenu = obj.submenu.map(subObj => 
                        subObj.label === e.target.name 
                            ? { ...subObj, checked: e.target.checked } 
                            : subObj
                    );
                    for(let i=0; i<updatedSubmenu.length; i++){
                        if(!updatedSubmenu[i].checked){
                            counter++;
                        }
                    };
                    if(counter===updatedSubmenu.length){
                        return { ...obj, checked: false, submenu: updatedSubmenu };
                    }
    
                    return { ...obj, checked: true, submenu: updatedSubmenu };
                } else {
                    return obj;
                }
            });
        }
    
        setSearchCategories(updated);
    }

    const [maxResultCount, setMaxResultCount] = useState(
        quickSearchFilter?.MaxResultCount
            ? quickSearchFilter.MaxResultCount
            : maxResultOptions[4]
    );
    function updateMaxResultCount(event, data) {
        setMaxResultCount(data);
    }

    useEffect(() => {
        const fetchData = async () => {
            const result = await fetchNewEntityTreeView(token);
            const config = {
                headers: { Authorization: "Bearer " + token }
            };
            searchCategories[0].submenu = [];
            if((entityIds && entityIds.length > 1) || CompanyManager || HoldingManager){
                setNewEntityTreeView(result.data);
                if(result.data){
                    const url = "OTEntity/getSearchSubMenuEntities";
                    if(CompanyManager || HoldingManager){
                        Axios.post(url, [], config).then(response => {
                            searchCategories[0].submenu = response.data;
                        })
                    }
                    else if(entityIds!==null && entityIds.length>1){
                        Axios.post(url, entityIds, config).then(response => {
                            searchCategories[0].submenu = response.data;
                        })
                    }
                }
                else{
                    const url = "OTEntity/getSearchEntities";
                    if(CompanyManager || HoldingManager){
                        Axios.post(url, [], config).then(response => {
                            for(let i=0; i<response.data.length; i++){
                                let data = { "checked": true, "label": response.data[i].Label, "value": response.data[i].Value};
                                searchCategories[0].submenu.push(data);
                            }
                        })
                    }
                    else if(entityIds!==null && entityIds.length>1){
                        Axios.post(url, entityIds, config).then(response => {
                            for(let i=0; i<response.data.length; i++){
                                let data = { "checked": true, "label": response.data[i].Label, "value": response.data[i].Value};
                                searchCategories[0].submenu.push(data);
                            }
                        })
                    }
                }
            }
        }
        fetchData();
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        let isSubscribed = true;
        if (isSubscribed && searchCategories && maxResultCount) {
            const quickSearchFilter = {
                SearchCategories: searchCategories,
                MaxResultCount: maxResultCount,
            };
            dispatch(updateQuickSearchFilter(quickSearchFilter));
        }

        return () => (isSubscribed = false);
    }, [searchCategories, maxResultCount, dispatch]);

    const handleSearch = event => {
        dispatch(updateQuickSearch(event.target.value));
        setSearchText(event.target.value);
    };

    function executeSearch(e) {
        if (e.key === "Enter" && searchText) {
            return historyPush(history, `/searchResults/${searchText}/${randomID()}`);
        }
    }

    const handlePopperClicAway = () => {
        setAnchorEl(null);
    };

    return (
        <Box
            component="div"
            sx={{
                position: "relative",
                borderRadius: "20px",
                border: 1,
                borderColor: alpha(theme.palette.primary.main, 0.15),
                "&:hover": {
                    borderColor: "primary.dark",
                },
                margin: "8px 16px 8px 8px",
                width: { xs: "100%", md: 340 },
            }}
        >
            <Box
                component="div"
                sx={{
                    height: 1,
                    position: "absolute",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexWrap: "wrap",
                }}
            ></Box>
            <InputBase
                value={searchText ?? ""}
                onChange={handleSearch}
                onKeyDown={executeSearch}
                fullWidth
                sx={{
                    color: "inherit",
                    "& .MuiInputBase-input": {
                        padding: theme.spacing(1, 1, 1, 0),
                        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
                        transition: theme.transitions.create("width"),
                    },
                }}
                endAdornment={
                    <>
                        <BasicTooltip
                            title={t("common.QuickSearchFilter")}
                            placement="top"
                        >
                            <IconButton onClick={e => handlePopper(e)}>
                                <FilterIcon />
                            </IconButton>
                        </BasicTooltip>
                        <Divider
                            orientation="vertical"
                            variant="fullWidth"
                            sx={{
                                height: 32,
                                m: "0px 8px",
                            }}
                        />
                        <BasicTooltip
                            title={t("common.GetResults")}
                            placement="top"
                        >
                            <IconButton
                                onClick={e =>
                                    searchText &&
                                    historyPush(history,
                                        `/searchResults/${searchText}/${randomID()}`
                                    )
                                }
                            >
                                <SearchIcon />
                            </IconButton>
                        </BasicTooltip>
                    </>
                }
                placeholder={`${t("common.Search")}...`}
                inputProps={{ "aria-label": "search" }}
            />

            <Popper
                open={openPopper}
                anchorEl={anchorEl}
                sx={{ zIndex: 9999 }}
                placement="bottom"
                id={id}
            >
                <ClickAwayListener onClickAway={() => handlePopperClicAway()}>
                    {searchCategories[0]?.submenu?.length < 2 ?
                        <Paper
                            elevation={6}
                            sx={{
                                borderRadius: "0px 0px 8px 8px",
                                margin: "0px 66px 0px 0px",
                                padding: 2,
                                width: 180
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        typography: "body2",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {t("common.QuickSearchInto")}
                                </Box>
                                <Box>
                                    {searchCategories?.map((item, index) => {
                                        return (
                                            <FormControl
                                                key={index}
                                                sx={{ width: 160 }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            name={item.label}
                                                            onChange={e => updateCategories(e)}
                                                            checked={item.checked}
                                                        />
                                                    }
                                                    label={t(`grid.title.${item.label}`)}
                                                />
                                            </FormControl>
                                        );
                                    })}
                                </Box>
                                <Select
                                    defaultValue={maxResultCount}
                                    sx={{ mt: 2 }}
                                    onChange={(event, data) =>
                                        updateMaxResultCount(event, data)
                                    }
                                    label={t("field.ResultsSize")}
                                    options={maxResultOptions}
                                />
                            </Box>
                        </Paper>
                    :
                    newEntityTreeView ? (
                        <Paper
                            elevation={6}
                            sx={{
                                borderRadius: "0px 0px 8px 8px",
                                margin: "0px 66px 0px 0px",
                                padding: 2,
                                width: 500,
                                maxHeight: 350,
                                overflowY: "auto"
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        typography: "body2",
                                        textTransform: "uppercase"
                                    }}
                                >
                                    {t("common.QuickSearchInto")}
                                </Box>
                                <Box>
                                    <div style={{ display: 'flex' }}>
                                        <div style={{ flex: 3 }}>
                                            <FormControl sx={{ width: 240 }}>
                                                <FormControlLabel
                                                    sx={{ mb:-1.4 }}
                                                    control={
                                                        <Checkbox
                                                            name={searchCategories[0].label}
                                                            onChange={e => updateCategories(e)}
                                                            checked={searchCategories[0].checked}
                                                        />
                                                    }
                                                    label={t(`grid.title.${searchCategories[0].label}`)}
                                                />
                                                {searchCategories[0]?.submenu?.length > 0 && (
                                                    <FormGroup sx={{ pl: 2 }}>
                                                        {searchCategories[0].submenu.map((branchItem, branchIndex) => (
                                                            <Box key={branchIndex}>
                                                                <Box
                                                                    sx={{
                                                                        typography: "body1",
                                                                        pt: 1,
                                                                        cursor: "pointer",
                                                                        display: "flex",
                                                                        alignItems: "center"
                                                                    }}
                                                                    onClick={() =>
                                                                        setSelectedBranch(
                                                                            selectedBranch === branchItem.BranchCode
                                                                                ? null
                                                                                : branchItem.BranchCode
                                                                        )
                                                                    }
                                                                >
                                                                    {selectedBranch === branchItem.BranchCode ? (
                                                                        <ArrowDropDownIcon sx={{ mr: 1 }} />
                                                                    ) : (
                                                                        <ArrowRightIcon sx={{ mr: 1 }} />
                                                                    )}
                                                                    {branchItem.BranchCode}
                                                                </Box>
                                                                {selectedBranch === branchItem.BranchCode &&
                                                                    branchItem.Countries.map((countryItem, countryIndex) => (
                                                                        <Box sx={{ ml:3.5, mb:1.5 }} key={countryIndex}>
                                                                            {countryIndex === 0 ?
                                                                                <Box sx={{ fontFamily:"arial", fontSize:"13px" }}>
                                                                                    <FiberManualRecordIcon
                                                                                        sx={{ fontSize:8, mt:1.5, mr:1, color:"gray" }}
                                                                                    />
                                                                                    {countryItem.CountryName}
                                                                                </Box>
                                                                                :
                                                                                <Box sx={{ fontFamily:"arial", fontSize:"13px" }}>
                                                                                    <FiberManualRecordIcon
                                                                                        sx={{ fontSize:8, mr:1, color:"gray" }}
                                                                                    />
                                                                                    {countryItem.CountryName}
                                                                                </Box>
                                                                            }
                                                                            {countryItem.OTEntities.map((subItem, subIndex) => (
                                                                                <FormControlLabel
                                                                                    key={subIndex}
                                                                                    control={
                                                                                        <Checkbox
                                                                                            name={subItem.Label}
                                                                                            onChange={(e) => updateCategories(e)}
                                                                                            checked={subItem.Checked}
                                                                                            sx={{ transform: "scale(0.9)" }}
                                                                                        />
                                                                                    }
                                                                                    label={subItem.Label}
                                                                                    sx={{
                                                                                        "& .MuiFormControlLabel-label": {
                                                                                            fontSize: 11.8,
                                                                                        },
                                                                                        mb: "-15px",
                                                                                        ml: 3
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    ))}
                                                            </Box>
                                                        ))}
                                                    </FormGroup>
                                                )}
                                            </FormControl>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {searchCategories.slice(1).map((item, index) => (
                                                <FormControl key={index} sx={{ width: 160 }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name={item.label}
                                                                onChange={e => updateCategories(e)}
                                                                checked={item.checked}
                                                            />
                                                        }
                                                        label={t(`grid.title.${item.label}`)}
                                                    />
                                                </FormControl>
                                            ))}
                                        </div>
                                    </div>
                                </Box>
                                <Select
                                    defaultValue={maxResultCount}
                                    sx={{ mt: 2 }}
                                    onChange={(event, data) =>
                                        updateMaxResultCount(event, data)
                                    }
                                    label={t("field.ResultsSize")}
                                    options={maxResultOptions}
                                />
                            </Box>
                        </Paper>)
                        :
                        (<Paper
                            elevation={6}
                            sx={{
                                borderRadius: "0px 0px 8px 8px",
                                margin: "0px 66px 0px 0px",
                                padding: 2,
                                width: 500,
                                maxHeight: 350,
                                overflowY: "auto"
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        typography: "body2",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {t("common.QuickSearchInto")}
                                </Box>
                                <Box>
                                    <div style={{ display: 'flex' }}>
                                        <div style={{ flex: 3 }}>
                                            <FormControl
                                                sx={{ width: 240 }}
                                            >
                                                <FormControlLabel
                                                    sx={{ mb:-1.4 }}
                                                    control={
                                                        <Checkbox
                                                            name={searchCategories[0].label}
                                                            onChange={e => updateCategories(e)}
                                                            checked={searchCategories[0].checked}
                                                        />
                                                    }
                                                    label={t(`grid.title.${searchCategories[0].label}`)}
                                                />
                                                {searchCategories[0].submenu && (
                                                    <FormGroup sx={{ pl: 1 }}>
                                                        {searchCategories[0].submenu.map((subItem, subIndex) => (
                                                            <FormControlLabel
                                                                key={subIndex}
                                                                control={
                                                                    <Checkbox
                                                                        name={subItem.label}
                                                                        onChange={e => updateCategories(e)}
                                                                        checked={subItem.checked}
                                                                        sx={{ transform: "scale(0.9)" }}
                                                                    />
                                                                }
                                                                label={subItem.label}
                                                                sx={{
                                                                    "& .MuiFormControlLabel-label": {
                                                                        fontSize: "12px",
                                                                    },
                                                                    ml: 1,
                                                                    mb: "-15px"
                                                                }}
                                                            />
                                                        ))}
                                                    </FormGroup>
                                                )}
                                            </FormControl>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {searchCategories.slice(1).map((item, index) => (
                                                <FormControl key={index} sx={{ width: 160 }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name={item.label}
                                                                onChange={e => updateCategories(e)}
                                                                checked={item.checked}
                                                            />
                                                        }
                                                        label={t(`grid.title.${item.label}`)}
                                                    />
                                                </FormControl>
                                            ))}
                                        </div>
                                    </div>
                                </Box>
                                <Select
                                    defaultValue={maxResultCount}
                                    sx={{ mt: 2 }}
                                    onChange={(event, data) =>
                                        updateMaxResultCount(event, data)
                                    }
                                    label={t("field.ResultsSize")}
                                    options={maxResultOptions}
                                />
                            </Box>
                        </Paper>
                    )}
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

export default SearchBar;
